<template>
  <!-- select box s -->
  <label v-if="label" :for="inputId" :class="['inp-label', {'a11y-blind': hideLabel}, labelClass]">
    {{ label }}
    <span v-if="astRequired" class="input-required">*</span>
    <span v-if="hasTooltip" class="has-tooltip">
      <img src="@/assets/images/pc/icon-hcon/icon-info.svg" width="20" height="20" alt="툴팁" class="tooltip-icon">
      <span :class="['tooltip', tooltipClass]">
        <slot name="tooltipContent"></slot>
      </span>
    </span>
  </label>
  <div 
    v-if="type === 'selectBox'"
    :class="['select-box', selectBoxTitleClass, { active: isActive }]"
    @mouseleave="isActive = false"
  >
    <button 
      type="button"
      class="select-label" 
      :style="{ color: selectedColor }"
      @click="isActive = !isActive" :disabled="(disabled || isReadonly)"
    > 
      <template v-if="optionType === 'product' && typeof selectedLabel === 'object'">
        {{ selectedLabel.text }} | 
        <strong class="font-bold">{{ selectedLabel.price.toLocaleString() }}원</strong>
        <em v-if="selectedLabel.percent" class="fs14 text-red ml4">{{ selectedLabel.percent }}%</em>
        <span v-if="selectedLabel.discount" class="fs14 ml4">({{ selectedLabel.discount.toLocaleString() }}원↓)</span>
      </template>
      <template v-else>
        {{ selectedLabel }}
      </template>
    </button>
    <ul v-if="optionType === 'product'" class="option-list">
      <li 
        v-for="(option, index) in productOptions" 
        :key="index"
        class="option-item"
        @click="selectOption(option as SelectedLabelType)"
      >
        {{ option.text }} | 
        <strong class="font-bold">{{ option.price.toLocaleString() }}원</strong>
        <em v-if="option.percent" class="fs14 text-red ml4">{{ option.percent }}%</em>
        <span v-if="option.discount" class="fs14 ml4">({{ option.discount.toLocaleString() }}원↓)</span>
      </li>
    </ul>
    <ul v-else class="option-list">
      <li 
        v-for="(option, index) in options" 
        :key="index"
        class="option-item"
        @click="selectOption(option as SelectedLabelType)"
      >
        {{ option }}
      </li>
    </ul>
  </div>
  <button type="button" :class="['btn-plus', plusButtonClass]" v-if="plusShowButton" @click="handlePlusBtn" :disabled="disabled">{{ plusButtonText }}</button>
  <!-- select box e -->
  <!-- family box s -->
  <div v-else-if="type === 'familyBox'" class="family-select-box" :class="[selectBoxTitleClass, { active: isActive }]" @mouseleave="isActive = false">
    <button 
      type="button"
      class="family-label" 
      :style="{ color: selectedColor }"
      @click="isActive = !isActive"
    >
      {{ props.defaultLabel }}
    </button>
    <ul class="family-option-list">
      <li 
        v-for="(option, index) in familyOptions" 
        :key="index"
        class="family-option-item"
        @click="selectOption(option)"
      >
        <NuxtLink :to="option.link" target="_blank" rel="noopenner noreferrer">{{ option.text }}</NuxtLink>
      </li>
    </ul>
  </div>
  <!-- family box e -->
  <!--
    dropdown box s
    - 기본 select box
    - 옵션: { key: 공통상세코드, value: 공통상세코드명 } 형식
  -->
  <div v-else-if="type === 'dropdownBox'" 
    :class="['select-box', selectBoxTitleClass, { active: isActive }, customClass]" 
    @mouseleave="isActive = false">
    <button 
      class="select-label" 
      :style="{ color: selectedColor }"
      @click="isActive = !isActive"
      :disabled="(disabled || isReadonly)"
    >
      {{ selectedValue }}
    </button>
    <ul class="option-list">
      <li 
        v-for="item in dropdownOptions" 
        :key="item.key"
        :value="item.value"
        class="option-item"
        @click="selectDropdownOption(item)"
      >
      {{ item.value }} <!-- 옵션 리스트에 보이는 값 -->
      </li>
    </ul>
  </div>
  <!-- dropdown box e -->
  <!--
    commDropdown box s
    - 공통코드용 select box
    - 옵션: { commDtlCd: 공통상세코드, commDtlCdNm: 공통상세코드명 } 형식
  -->
  <div v-else-if="type === 'commDropdownBox'" 
    :class="['select-box', selectBoxTitleClass, { active: isActive }, customClass]" 
    @mouseleave="isActive = false" >
    <button 
      class="select-label" 
      :style="{ color: selectedColor }"
      @click="isActive = !isActive"
      :disabled="(disabled || isReadonly)"
    >
      {{ selectedCommValue }}
    </button>
    <ul class="option-list">
      <li 
        v-for="item in commDropdownOptions" 
        :key="item.commDtlCd"
        :value="item.commDtlCdNm"
        class="option-item"
        @click="selectCommDropdownOption(item)"
      >
      {{ displayOptions === 'commDtlCd' ? item.commDtlCd : item.commDtlCdNm}} <!-- 옵션 리스트에 보이는 값 -->
      </li>
    </ul>
  </div>
  <!-- commDropdown box e -->
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';

const inputId = useId();
const emit = defineEmits(['update:modelValue', 'plusBtnClick']);

type SelectBoxType = 'selectBox' | 'familyBox' | 'dropdownBox' | 'commDropdownBox';
type OptionType = 'default' | 'product';
type SelectedLabelType = string | number | ProductOptionData;

export interface ProductOptionData {
  text: string
  price: number
  percent?: string | number
  discount?: number
  hcGdsCd?: string
}

const props = defineProps({
  type: {
    type: String as () => SelectBoxType,
    default: 'text' as SelectBoxType,
    validator: (value: string):value is SelectBoxType => ['selectBox', 'familyBox', 'dropdownBox', 'commDropdownBox'].includes(value)
  },
  label: {
    type: String,
    default: ''
  },
  hideLabel: {
    type: Boolean,
    default: false
  },
  labelClass: {
    type: String,
    default: ''
  },
  options: {
    type: Array,
    default: () => []
  },
  defaultLabel: {
    type: String,
    default: '드롭다운메뉴'
  },
  readonly: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  selectBoxTitleClass: {
    type: String,
    default: ''
  },
  astRequired: {
    type: Boolean,
    default: false
  },
  hasTooltip: {
    type: Boolean,
    default: false
  },
  tooltipClass: {
    type: String,
    default: ''
  },
  familyOptions: {
    type: Object,
    default: () => [
      { link: '/', text: '옵션리스트1'},
      { link: '/', text: '옵션리스트2'},
      { link: '/', text: '옵션리스트3'},
      { link: '/', text: '옵션리스트4'}
    ]
  },
  dropdownOptions: {
    type: Object,
    default: () => [
      { key: 'value', value: 'label' },
    ]
  },
  commDropdownOptions: {
    type: Object,
    default: () => [
      { key: 'value', value: 'label' },
    ]
  },
  displayOptions: {
    type: String,
    default: 'commDtlNm'
  },
  modelValue: {
    type: [String, Number, null, Object],
    default: ''
  },
  placeholder: {
    type: String,
    default: '선택'
  },
  plusButtonClass: {
    type: String,
    default: ''
  },
  plusShowButton: {
    type: Boolean,
    default: false
  },
  plusButtonText: {
    type: String,
    default: ''
  },
  customClass: {
    type: [Boolean, String, Object],
    default: () => ({})
  },
  optionType: {
    type: String as () => OptionType,
    default: 'default'
  },
  productOptions: {
    type: Object,
    default: () => [
      { text: '옵션리스트1', price: 9000, percent: 10, discount: 1000 },
      { text: '옵션리스트2', price: 8900, percent: 11, discount: 1100},
      { text: '옵션리스트3', price: 8800, percent: 12, discount: 1200},
      { text: '옵션리스트4', price: 8700, percent: 13, discount: 1300}
    ]
  },
});

const isActive = ref(false);
const selectedLabel = ref<SelectedLabelType>(props.defaultLabel);
const selectedColor = ref('');
const isReadonly = computed(() => props.readonly)

const selectOption = (option: SelectedLabelType) => {
  selectedLabel.value = option;
  selectedColor.value = '#222';
  isActive.value = false;
  emit('update:modelValue', option);
  
};

// type === 'dropdownBox' 인 경우, 선택한 값 세팅
const selectedValue = computed(() => {
  const selected = props.dropdownOptions.find((item: { key: string; }) => item.key === props.modelValue);
  return selected ? selected.value : props.placeholder;
});

// type === 'commDropdownBox' 인 경우, 선택한 값 세팅
const selectedCommValue = computed(() => {
  const selected = props.commDropdownOptions.find((item: { commDtlCd: string; }) => item.commDtlCd === props.modelValue);
  if(selected && props.displayOptions == 'commDtlCd') {
    return selected.commDtlCd
  } 
  else if(selected) {
    return selected.commDtlCdNm
  } 
  else {
    return props.placeholder
  }
});

// type === 'dropdownBox' 인 경우, 선택한 값 emit
const selectDropdownOption = (option: any) => {
  selectedLabel.value = option;
  selectedColor.value = '#222';
  isActive.value = false;
  emit('update:modelValue', option.key);
};

// type === 'commDropdownBox' 인 경우, 선택한 값 emit
const selectCommDropdownOption = (option: any) => {
  selectedLabel.value = option;
  selectedColor.value = '#222';
  isActive.value = false;
  emit('update:modelValue', option.commDtlCd);
};

// 부모컴포넌트에서 해당 함수 사용
defineExpose({
  
})

// plus 버튼 클릭 이벤트
const handlePlusBtn = () => {
  emit('plusBtnClick')
}

</script>

<style lang="scss" scoped>
@use "@/assets/scss/pc/order-hcon.scss" as *;
</style>